import React, { useContext, useReducer, useEffect, useMemo, useCallback } from "react";
import Cookies from "js-cookie";
import { AuthReducer } from "./AuthReducer";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getTenantFromRequest, getTenantBrandingColor, getTenantAutoApproveUser, getTenantAllowGuestUpload } from "../utils/tenantConfig";
import { useUserProfile } from "../hooks/useUserProfile";
import { pruneExpired, clearGuestLayouts } from "../utils/guestLayoutStorage";
import { resolveInitialAllowGuestUpload, shouldGateBoot } from "../utils/bootGate";

const AppContext = React.createContext();

const AuthProvider = ({ children }) => {
  const API_BASE = process.env.REACT_APP_ROOM_NATE_API;
  const API_VERSION = process.env.REACT_APP_ROOM_NATE_VERSION;
  axios.defaults.baseURL = `${(API_BASE ?? "").replace(/\/$/, "")}/${API_VERSION ?? ""}`;

  const navigate = useNavigate();

  const Initialization = {
    currentUserSessionStorage: (() => {
      const token = Cookies.get("token");
      try {
        if (!token || token === "undefined") return null;
        return JSON.parse(token);
      } catch (err) {
        console.error("Invalid token cookie:", err);
        return null;
      }
    })(),
    brandingColor: (() => {
      const brandingColor = Cookies.get("branding-color");
      try {
        if (!brandingColor || brandingColor === "undefined") return null;
        return brandingColor;
      } catch (err) {
        console.error("Invalid Branding Color:", err);
        return null;
      }
    })(),
    partner: Cookies.get("partner"),
    // Seed the guest-upload flag synchronously (cookie → static tenant default)
    // so guest routing at "/" and "/main" is correct on first paint without
    // waiting on /partners/domain. The fetch below reconciles it every boot.
    allowGuestUpload: resolveInitialAllowGuestUpload({
      cookieValue: Cookies.get("allowGuestUpload"),
      tenantAllowGuestUpload: (() => {
        try {
          return getTenantAllowGuestUpload();
        } catch {
          return false;
        }
      })(),
    }),
    autoApproveUser: Cookies.get("autoApproveUser") === "true",
    partnerPackage: (() => {
      const pkg = Cookies.get("partner-package");
      try {
        if (!pkg || pkg === "undefined" || pkg[0] !== '{') return null;
        return JSON.parse(pkg);
      } catch (err) {
        console.error("Invalid partner-package cookie:", err);
        return null;
      }
    })(),
    partnerName: Cookies.get("partner-name"),
    // Branding NEVER blocks the app anymore. This flag now only briefly defers
    // the guest redirect on the two entry routes ("/" and "/main"), and only for
    // a true first visit to an unrecognized domain (no known tenant, no cached
    // partner cookies). Known tenants (e.g. serhant) and returning users render
    // instantly. A watchdog in the boot effect bounds the wait. See bootGate.js.
    loaderDomain: (() => {
      try {
        return shouldGateBoot({
          tenantId: getTenantFromRequest(),
          hasBrandingCookie: Boolean(Cookies.get("branding-color")),
          hasGuestUploadCookie: Boolean(Cookies.get("allowGuestUpload")),
        });
      } catch {
        return false;
      }
    })()
  };
  
  const [state, dispatch] = useReducer(AuthReducer, Initialization);
  
  // Initialize global user profile hook - this will fetch user data once when logged in
  useUserProfile();

  const Logout = useCallback((navigateValue) => {
    if (navigateValue) {
      navigate("/signup");
    }
    Cookies.remove("token");
    sessionStorage.removeItem("redirectPath");
    sessionStorage.removeItem("needsCopy");
    sessionStorage.removeItem("layoutIdToCopy");
    // Clear any guest claim intents so a shared browser doesn't leak a prior
    // guest's uuids to the next person who signs up/logs in.
    clearGuestLayouts();

    dispatch({ type: "LOGOUT", payload: null });
  }, [navigate]);

  const getBrandingColor = async () => {
    // Check for tenant-specific branding first (acts as fallback values).
    const tenantId = getTenantFromRequest();
    let tenantBrandingColor;
    let tenantAutoApproveUser;
    if (tenantId) {
      tenantBrandingColor = getTenantBrandingColor(tenantId);
      tenantAutoApproveUser = getTenantAutoApproveUser(tenantId);

      dispatch({ type: "SETCOLOR", payload: tenantBrandingColor });
      dispatch({ type: "AUTOAPPROVEUSER", payload: tenantAutoApproveUser });

      Cookies.set("branding-color", tenantBrandingColor, {
        expires: 30,
      });

      Cookies.set("autoApproveUser", tenantAutoApproveUser, {
        expires: 30,
      });
    }

    const currentUrl = window.location.href;

    const parts = currentUrl?.split("//")[1]?.split("/");

    const domain = parts?.[0];

    let defaultPackage = {
      "name": "ALLOW ALL FEATURES AND SHOW IFRAMES",
      "iframeVisibility": true,
      "productVisibility": false,
      "allowMakeMagic": true,
      "allowManualItems": true,
  }

    try {
      const color = await axios.get(`/partners/domain/${domain}`, { timeout: 4000 });

      dispatch({ type: "LOADERDOMAIN", payload: false});


      const updatedPackage = color?.data?.isSelf
      ? {
          ...defaultPackage,
          iframeVisibility: false,
          productVisibility: true,
        }
      : defaultPackage;

        dispatch({ type: "SETCOLOR", payload: color?.data?.primaryColor || tenantBrandingColor });
        dispatch({ type: "PARNTERNAME", payload: color?.data?.title });

        Cookies.set("partner-name", color?.data?.title, {
          expires: 30,
        });

        Cookies.set("branding-color", color?.data?.primaryColor || tenantBrandingColor, {
          expires: 30,
        });

        dispatch({ type: "PARTNERPACKAGE", payload: color?.data?.partnerPackage ||  updatedPackage});

        Cookies.set("partner-package", JSON.stringify(color?.data?.partnerPackage || updatedPackage), {
          expires: 30,
        });

        dispatch({ type: "ALLOWGUESTUPLOAD", payload: color?.data?.allowGuestUpload});

        Cookies.set("allowGuestUpload", color?.data?.allowGuestUpload, {
          expires: 30,
        });


        dispatch({
          type: "AUTOAPPROVEUSER",
          payload:
            typeof color?.data?.autoApproveUser === "boolean"
              ? color.data.autoApproveUser
              : tenantAutoApproveUser,
        });

        Cookies.set(
          "autoApproveUser",
          typeof color?.data?.autoApproveUser === "boolean"
            ? color.data.autoApproveUser
            : tenantAutoApproveUser,
          {
          expires: 30,
          }
        );

        dispatch({ type: "SETPARTNER", payload: color?.data?._id });

        Cookies.set("partner", color?.data?._id, {
          expires: 30,
        });
    } catch (error) {
      console.error("Error fetching branding color:", error);
    } finally {
      dispatch({ type: "LOADERDOMAIN", payload: false});
    }
  };

useEffect(() => {
  // Drop expired guest claim entries on app boot.
  pruneExpired();

  if (window.location.hostname === "backup.magicfurnish.com") {
    dispatch({ type: "LOADERDOMAIN", payload: false });
    return;
  }

  // Safety net: never let the branding/flags fetch defer the guest entry routes
  // for longer than this, even if the backend is cold/slow right after a deploy.
  // Set just above the axios timeout so a real response is preferred whenever it
  // arrives; this only fires if the request hangs past its own timeout.
  const loaderWatchdog = setTimeout(() => {
    dispatch({ type: "LOADERDOMAIN", payload: false });
  }, 5000);

  getBrandingColor().finally(() => clearTimeout(loaderWatchdog));

  return () => clearTimeout(loaderWatchdog);
}, []);

  const contextValue = useMemo(() => ({
    currentUserSessionStorage: state.currentUserSessionStorage,
    brandingColor: state.brandingColor,
    partner: state.partner,
    partnerPackage: state.partnerPackage,
    allowGuestUpload: state.allowGuestUpload,
    autoApproveUser: state.autoApproveUser,
    partnerName: state.partnerName,
    loaderDomain: state.loaderDomain,
    dispatch,
    Logout,
  }), [
    state.currentUserSessionStorage,
    state.brandingColor,
    state.partner,
    state.partnerPackage,
    state.allowGuestUpload,
    state.autoApproveUser,
    state.partnerName,
    state.loaderDomain,
    Logout
  ]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const useGlobalAuthContext = () => {
  return useContext(AppContext);
};

export { AppContext, AuthProvider };
